import { useState, type FormEvent } from "react";
import type { CommunityComment } from "@/mocks/community";
import { formatCount } from "@/components/base/PostCard";

interface CommentSectionProps {
  initialComments: CommunityComment[];
  totalCount: number;
}

const avatarPalette = ["bg-primary-500", "bg-secondary-500", "bg-accent-600", "bg-foreground-700"];

const MAX_LENGTH = 200;

export default function CommentSection({ initialComments, totalCount }: CommentSectionProps) {
  const [comments, setComments] = useState<CommunityComment[]>(initialComments);
  const [nickname, setNickname] = useState("");
  const [content, setContent] = useState("");
  const [error, setError] = useState("");
  const [hint, setHint] = useState("");

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmed = content.trim();
    if (!trimmed) {
      setError("说点什么再发送吧。");
      setHint("");
      return;
    }
    if (trimmed.length > MAX_LENGTH) {
      setError(`评论最多 ${MAX_LENGTH} 字，当前 ${trimmed.length} 字。`);
      setHint("");
      return;
    }
    const newComment: CommunityComment = {
      id: `local-${Date.now()}`,
      user: nickname.trim() || "匿名围观群众",
      avatarClass: avatarPalette[comments.length % avatarPalette.length],
      time: "刚刚",
      content: trimmed,
      likes: 0,
    };
    setComments((prev) => [newComment, ...prev]);
    setContent("");
    setNickname("");
    setError("");
    setHint("发送成功，你的观点已经出现在讨论区顶部。");
  };

  return (
    <section id="comments" className="mt-10">
      <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950 md:text-xl">
        <i className="ri-chat-3-line text-primary-500" aria-hidden="true" />
        评论区
        <span className="text-sm font-normal text-foreground-500">({formatCount(totalCount)} 条讨论)</span>
      </h2>

      <form
        onSubmit={handleSubmit}
        className="mt-5 rounded-xl border border-background-200 bg-background-100 p-4 md:p-5"
      >
        <label htmlFor="comment-nickname" className="text-sm font-medium text-foreground-800">
          昵称（选填）
        </label>
        <input
          id="comment-nickname"
          name="nickname"
          type="text"
          value={nickname}
          onChange={(e) => setNickname(e.target.value)}
          placeholder="留空则以匿名身份发言"
          className="mt-2 w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
        />

        <label htmlFor="comment-content" className="mt-4 block text-sm font-medium text-foreground-800">
          你的看法
        </label>
        <textarea
          id="comment-content"
          name="content"
          rows={4}
          maxLength={MAX_LENGTH}
          value={content}
          onChange={(e) => {
            setContent(e.target.value);
            if (error) setError("");
          }}
          placeholder="理性讨论，尊重当事人，避免无依据的猜测。"
          className="mt-2 w-full resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm leading-relaxed text-foreground-900 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
        />
        <div className="mt-2 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <p className={`text-xs ${error ? "text-primary-600" : "text-foreground-500"}`}>
            {error || hint || `已输入 ${content.length}/${MAX_LENGTH} 字`}
          </p>
          <button
            type="submit"
            className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            <i className="ri-send-plane-fill" aria-hidden="true" />
            发表评论
          </button>
        </div>
      </form>

      <ul className="mt-6 space-y-4">
        {comments.map((comment) => (
          <li key={comment.id} className="rounded-xl border border-background-200 bg-background-50 p-4 md:p-5">
            <div className="flex items-center gap-3">
              <span
                className={`flex h-9 w-9 items-center justify-center rounded-full text-sm font-semibold text-background-50 ${comment.avatarClass}`}
                aria-hidden="true"
              >
                {comment.user.slice(0, 1)}
              </span>
              <div>
                <p className="text-sm font-medium text-foreground-900">{comment.user}</p>
                <p className="text-xs text-foreground-500">{comment.time}</p>
              </div>
              <span className="ml-auto inline-flex items-center gap-1 text-xs text-foreground-500">
                <i className="ri-thumb-up-line" aria-hidden="true" />
                {comment.likes}
              </span>
            </div>
            <p className="mt-3 text-sm leading-relaxed text-foreground-700">{comment.content}</p>
          </li>
        ))}
      </ul>
    </section>
  );
}